import Immutable from 'immutable';
import React from 'react';

import AppendToBody from 'sharedreact/components/AppendToBody';
import MessageDialog from 'sharedreact/components/MessageDialog';
import ParameterSet from 'sharedreact/components/ParameterSet';
import ResourceFieldWithState from 'sharedreact/components/ResourceFieldWithState';
import Tab from 'sharedreact/components/Tab';
import TextField from 'sharedreact/components/TextField';
import TextInfoField from 'sharedreact/components/TextInfoField';
import ToolButton from 'sharedreact/components/ToolButton';
import Toolbar from 'sharedreact/components/Toolbar';
import ToolbarGroup from 'sharedreact/components/ToolbarGroup';
import ToolbarTitle from 'sharedreact/components/ToolbarTitle';
import ValidationForm from 'sharedreact/components/ValidationForm';
import WarningPanel from 'sharedreact/components/WarningPanel';
import type ErrorData from 'sharedreact/data/ErrorData';
import type User from 'sharedreact/data/User';
import { translate } from 'sharedreact/utils/LanguageSupport';

import UserSelectionDialogContainer from 'workspaces/common/containers/users/UserSelectionDialogContainer';

import CopyTokenContainer from 'workspaces/launcher/containers/admin/CopyTokenContainer';
import Token from 'workspaces/launcher/data/Token';

type Props = {
  index: number,
  activeTab: number,
  action: string,
  userId?: string,
  token: Token | null,
  language: string,
  showClipboardMsg: boolean,
  needsConfirm: boolean,
  saveMsg: string,
  user?: User,

  onCreate: (id: string, tags: string) => void,
  onSave: (id: string, tags: string) => void,
  onGoBack: () => void,
  onCloseClipboardMsg: () => void,
  onGetUserSuggestions: (filterString: string | null, onSuccess: (items: Immutable.List<User>, totalCount: number) => void, onFailure:(error: ErrorData) => void) => void,
  onCancelOverwrite: () => void,
  onConfirmOverwrite: (id: string, tags: string) => void
};

type State = {
  user: User | null,
  tags: string,
  showUserSelection: boolean,
  filterString: string
};

class TokenDetailsTab extends React.Component<Props, State> {
  constructor (props: Props) {
    super(props);

    this.state = {
      user: null,
      tags: '',
      showUserSelection: false,
      filterString: ''
    };

    this.handleGenToken = this.handleGenToken.bind(this);
    this.handleSaveToken = this.handleSaveToken.bind(this);
    this.handleChangeValue = this.handleChangeValue.bind(this);
    this.handleCloseClipboardMsg = this.handleCloseClipboardMsg.bind(this);
    this.handleHideReceiverSelectionDialog = this.handleHideReceiverSelectionDialog.bind(this);
    this.handleSelectReceiver = this.handleSelectReceiver.bind(this);
    this.handleConfirmOverwrite = this.handleConfirmOverwrite.bind(this);
  }

  updateState () {
    this.setState({user: this.props.user ?? null, tags: this.props.token ? this.props.token.tags : ''});
  }

  override componentDidMount (): void {
    if (this.props.activeTab === this.props.index) {
      this.updateState();
    }
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if (prevProps.activeTab !== this.props.activeTab && this.props.activeTab === this.props.index) {
      this.updateState();
    }
  }

  handleGenToken (): void {
    if (this.state.user != null) {
      this.props.onCreate(this.state.user.id, this.state.tags);
    }
  }

  handleSaveToken (userId: string | null): void {
    if (userId != null) {
      this.props.onSave(userId, this.state.tags);
    }
  }

  handleCloseClipboardMsg (): void {
    this.props.onCloseClipboardMsg();
  }

  handleChangeValue (
    name: string,
    value: any
  ) {
    if (name === 'user') {
      this.setState({user: value});
    } else if (name === 'tags') {
      this.setState({tags: value});
    }
  }

  handleShowUserSelectionDialog (filterString: string | null): void {
    this.setState({showUserSelection: true, filterString: filterString != null ? filterString : ''});
  }

  handleHideReceiverSelectionDialog () {
    this.setState({showUserSelection: false});
  }

  handleSelectReceiver (user: User) {
    this.setState({user: user});
  }

  handleConfirmOverwrite () {
    if (this.state.user != null) {
      this.props.onConfirmOverwrite(this.state.user.id, this.state.tags);
    }
  }

  override render (): React.ReactNode {
    const {
      index, activeTab, action,
      userId,
      token,
      onGoBack,
      showClipboardMsg,
      needsConfirm,
      saveMsg,
      onGetUserSuggestions,
      onCancelOverwrite,
      language // eslint-disable-line no-unused-vars
    } = this.props;

    const isCreateDisabled = this.state.user == null;

    return (
      <ValidationForm withProvider>
        <Tab index={index} activeIndex={activeTab}>
          <Toolbar>
            <ToolbarGroup>
              <ToolbarGroup>
                {action === 'create'
                ? <ToolbarTitle title={translate('launcher.admin.tokens.newtoken')} />
                : <ToolbarTitle title={translate('launcher.admin.tokens.edittoken')} />
                }
              </ToolbarGroup>
              <ToolbarGroup align='right'>
                {saveMsg !== '' && <span id='ae_token_save_status' className='token_status'>
                  {translate(saveMsg)}
                </span>}
                {action === 'create'
                ? <ToolButton
                  id='ae_button_token_generate'
                  onlyShowIconWhenCollapsed
                  icon='bi bi-floppy-fill'
                  type='btn-primary'
                  onClick={this.handleGenToken}
                  disabled={isCreateDisabled}
                >
                  {translate('launcher.admin.tokens.generate')}
                </ToolButton>
                : <ToolButton
                  id='ae_button_token_edit'
                  onlyShowIconWhenCollapsed
                  icon='bi bi-floppy-fill'
                  type='btn-primary'
                  onClick={() => this.handleSaveToken(userId ?? null)}
                >
                  {translate('launcher.admin.tokens.save')}
                </ToolButton>
                }
                <ToolbarGroup align='right' className='esko_toolbarclose'>
                  <ToolButton
                    id='ae_button_token_close'
                    icon='bi bi-x'
                    className='esko_icononlybutton'
                    onClick={onGoBack}
                  />
                </ToolbarGroup>
              </ToolbarGroup>
            </ToolbarGroup>
          </Toolbar>
          <AppendToBody>
            <CopyTokenContainer userName={this.state.user != null ? this.state.user.name : ''} />
            <MessageDialog
              language={language}
              title={translate('launcher.admin.tokens.confirmoverwrite.title')}
              message={translate('launcher.admin.tokens.confirmoverwrite.text', this.state.user != null ? this.state.user.name : '')}
              show={needsConfirm}
              onClose={onCancelOverwrite}
              onConfirm={this.handleConfirmOverwrite}
            />
            <MessageDialog
              language={language}
              title={translate('launcher.admin.tokens.copiedtoclipboard.title')}
              message={translate('launcher.admin.tokens.copiedtoclipboard.text', this.state.user != null ? this.state.user.name : '')}
              show={showClipboardMsg}
              onClose={this.handleCloseClipboardMsg}
            />
            <UserSelectionDialogContainer
              show={this.state.showUserSelection}
              title={translate('launcher.admin.tokens.selectuser')}
              selection={this.state.user != null ? Immutable.Set([this.state.user.id]) : Immutable.Set()}
              onClose={this.handleHideReceiverSelectionDialog}
              onSelect={this.handleSelectReceiver}
              filterString={this.state.filterString}
            />
          </AppendToBody>
          <div className='webappslauncher-scroll-container'>
            <div className='webappslauncher-stretch-container'>
              <ParameterSet className='webappslauncher-text-small-gaps'>
                {action === 'create'
                ? <ResourceFieldWithState<User> id='ae_input_tokens_user'
                  name='user'
                  label={translate('launcher.admin.tokens.user')}
                  value={this.state.user}
                  placeholder={translate('common.user.none')}
                  format={(user: User | null) => user
                    ? (user.name === 'Any user...'
                        ? translate('common.todos.anyuser')
                        : user.name)
                    : ''}
                  onChangeValue={this.handleChangeValue}
                  onShowResources={(filterString: string | null, name: string) => {
                    this.handleShowUserSelectionDialog(filterString);
                  }}
                  onGetSuggestions={onGetUserSuggestions}
                  validations={['required']}
                />
                : <TextInfoField id='ae_info_tokens_username'
                  label={translate('launcher.admin.tokens.user')}
                  value={token ? token.userName : ''}
                />
                }
                <TextField id='ae_input_tokens_tags'
                  className='esko_parameter-input-max500'
                  name='tags'
                  label={translate('launcher.admin.tokens.tags')}
                  value={this.state.tags}
                  onChangeValue={this.handleChangeValue}
                />
              </ParameterSet>
              <div className='webappslauncher-warning'>
                <WarningPanel text={translate('launcher.admin.tokens.warning')} />
              </div>
            </div>
          </div>
        </Tab>
      </ValidationForm>
    );
  }
}

export default TokenDetailsTab;
